import { FC, useEffect, useMemo, useRef, useState } from "react";
import { expertImage1 } from "~/assets/illustrations";
import { type ChatContext } from "~/hooks/useChatbotContext";

const APPOINTMENT_SUGGESTIONS = [
  {
    category: "Patient & Case",
    prompts: [
      "Summarize the patient's case and injury details",
      "What are the upcoming appointment details?",
    ],
  },
  {
    category: "Medical Records",
    prompts: [
      "What diagnostic studies are on file?",
      "Summarize the history of injury",
    ],
  },
  {
    category: "Scheduling",
    prompts: [
      "Show the doctor's availability this week",
      "List upcoming appointments for this doctor",
    ],
  },
  {
    category: "Zendesk",
    prompts: [
      "Search for Zendesk tickets related to this case",
      "Create a Zendesk ticket for this appointment",
    ],
  },
  {
    category: "Notion",
    prompts: [
      "Search Notion for intake protocols",
      "Create a Notion page with appointment notes",
    ],
  },
  {
    category: "Slack",
    prompts: [
      "Search Slack for recent discussions about this case",
      "What was discussed in #ops_general today?",
    ],
  },
  {
    category: "Linear",
    prompts: [
      "Search Linear for issues related to this case",
      "Create a bug ticket on Linear and assign to me",
    ],
  },
  {
    category: "Google Workspace",
    prompts: [
      "Search Google Drive for related documents",
      "Create a Google Doc with notes from this appointment",
    ],
  },
];

const DOC_SUGGESTIONS = [
  {
    category: "Indigo Doc",
    prompts: [
      "Summarize this document",
      "Suggest improvements to this document",
      "Help me format this document",
    ],
  },
];

const GENERAL_SUGGESTIONS = [
  {
    category: "Doctors & Locations",
    prompts: ["Search for a doctor by name", "Look up an office location"],
  },
  {
    category: "Scheduling",
    prompts: [
      "Check a doctor's availability",
      "List appointments for a doctor this week",
    ],
  },
  {
    category: "Zendesk",
    prompts: [
      "Search Zendesk tickets by keyword",
      "Create a new Zendesk support ticket",
    ],
  },
  {
    category: "Notion",
    prompts: [
      "Search Notion for a process or protocol",
      "Create a new page in Notion",
    ],
  },
  {
    category: "Slack",
    prompts: [
      "Search Slack for recent team discussions",
      "What's been posted in #ops_updates recently?",
    ],
  },
  {
    category: "Linear",
    prompts: [
      "Search Linear for recent issues",
      "Create a bug ticket on Linear and assign to me",
    ],
  },
  {
    category: "Google Workspace",
    prompts: [
      "Find a document on Google Drive",
      "Create a new spreadsheet for tracking",
    ],
  },
];

const ROTATE_INTERVAL = 5000;
const FADE_DURATION = 900;

const GREETING_QUESTIONS = [
  "What can I help with?",
  "Where should we start?",
  "What's on your plate?",
  "What's first?",
  "How can I help?",
  "What are we working on?",
  "Where to?",
];

const timeOfDayPrefix = (hour: number): string => {
  if (hour >= 22 || hour < 5) return "Still here";
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  return "Evening";
};

type Greeting = { lead: string | null; question: string };

// Question rotates once per user-local day; prefix tracks their clock.
const buildGreeting = (firstName: string | null | undefined): Greeting => {
  const now = new Date();
  const localMidnightMs = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const dayIndex = Math.floor(localMidnightMs / 86_400_000);
  const question = GREETING_QUESTIONS[dayIndex % GREETING_QUESTIONS.length];
  if (!firstName) return { lead: null, question };
  return {
    lead: `${timeOfDayPrefix(now.getHours())}, ${firstName}.`,
    question,
  };
};

export const ChatbotEmptyState: FC<{
  context: ChatContext | null;
  userFirstName?: string | null;
  onSelectSuggestion: (prompt: string) => void;
}> = ({ context, userFirstName, onSelectSuggestion }) => {
  const suggestions =
    context?.type === "indigo_doc"
      ? DOC_SUGGESTIONS
      : context?.type === "appointment"
        ? APPOINTMENT_SUGGESTIONS
        : GENERAL_SUGGESTIONS;
  const [activeIndex, setActiveIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [chipsVisible, setChipsVisible] = useState(false);
  const pausedRef = useRef(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable across re-renders; recomputed only when the user's name arrives.
  const greeting = useMemo(() => buildGreeting(userFirstName), [userFirstName]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    const delay = setTimeout(() => setChipsVisible(true), 1000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(delay);
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    setActiveIndex(0);
    setFading(false);
  }, [context]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (pausedRef.current) return;
      setFading(true);
      fadeTimerRef.current = setTimeout(() => {
        if (!pausedRef.current) {
          setActiveIndex((prev) => (prev + 1) % suggestions.length);
        }
        setFading(false);
        fadeTimerRef.current = null;
      }, FADE_DURATION);
    }, ROTATE_INTERVAL);
    return () => {
      clearInterval(timer);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [suggestions.length]);

  return (
    // The `h-full` box stays untransformed, and the entry slide rides on the
    // inner wrapper instead. A transform does not move layout, but it does
    // enlarge an ancestor's scrollable overflow region — so sliding a box that
    // is already exactly the scroll container's height made the panel report
    // 8px of overflow and paint a scrollbar for the length of the fade, on a
    // panel showing nothing but a greeting. The inner wrapper is shorter than
    // the container, so the same slide happens into slack that is already
    // there. On a panel too short to hold the greeting the bar comes back —
    // correctly, since that panel is genuinely scrolling.
    <div className="flex flex-col items-center justify-center h-full px-4">
      <div
        className="flex flex-col items-center w-full"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? "translateY(0)" : "translateY(8px)",
          transition: `opacity ${FADE_DURATION}ms ease, transform ${FADE_DURATION}ms ease`,
        }}
      >
        <img
          src={expertImage1}
          alt="Expert illustration"
          className="chatbot-empty-state-expert"
          width={109}
          height={109}
        />
        <span
          className="mt-1"
          style={{
            fontFamily: "'Society', serif",
            fontSize: 24,
            fontStyle: "italic",
            fontWeight: 300,
            lineHeight: 1.15,
            color: "var(--indigo-text-primary)",
            textAlign: "center",
          }}
        >
          {greeting.lead && (
            <>
              <span className="whitespace-nowrap">{greeting.lead}</span>{" "}
            </>
          )}
          <span className="whitespace-nowrap">{greeting.question}</span>
        </span>
        <span
          className="text-xs mt-2 mb-5"
          style={{
            color: "var(--indigo-text-tertiary)",
          }}
        >
          I can look things up, take action, and do the busy work.
        </span>
        <div
          className="w-full max-w-md text-center grid"
          style={{
            opacity: chipsVisible ? 1 : 0,
            transition: `opacity ${FADE_DURATION}ms ease`,
          }}
          onMouseEnter={() => {
            pausedRef.current = true;
            if (fadeTimerRef.current) {
              clearTimeout(fadeTimerRef.current);
              fadeTimerRef.current = null;
              setFading(false);
            }
          }}
          onMouseLeave={() => (pausedRef.current = false)}
        >
          {suggestions.map((group, i) => (
            <div
              key={group.category}
              aria-hidden={i !== activeIndex}
              style={{
                gridArea: "1 / 1",
                opacity: !fading && i === activeIndex ? 1 : 0,
                transition: `opacity ${FADE_DURATION}ms ease`,
                pointerEvents: i === activeIndex ? "auto" : "none",
              }}
            >
              <span
                className="text-[11px] font-medium tracking-wide"
                style={{ color: "var(--indigo-text-tertiary)" }}
              >
                {group.category}
              </span>
              <div className="flex flex-wrap justify-center gap-1.5 mt-1">
                {group.prompts.map((prompt) => (
                  <button
                    key={prompt}
                    className="chatbot-suggestion-chip px-2.5 py-1 rounded-full text-xs"
                    style={{ color: "var(--indigo-gray-600)" }}
                    onClick={() => onSelectSuggestion(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
